import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  formatDetailedTimestamp,
  formatRelativeTime,
} from '../utils/fliqueDates';
import { userTabInitials } from '../utils/userInitials';
import ReportModal from './ReportModal';
import ThreeDotMenu from './ThreeDotMenu';
import { useToast } from '../context/ToastContext';

async function readJson(res) {
  try {
    return await res.json();
  } catch (_e) {
    return {};
  }
}

function insertCommentWithThreadOrder(existingComments, newComment) {
  if (!newComment) return existingComments;

  const parentId = newComment.parentCommentId;
  if (!parentId) {
    return [newComment, ...existingComments];
  }

  const parentIndex = existingComments.findIndex(
    (comment) => String(comment.id) === String(parentId),
  );
  if (parentIndex === -1) {
    return [...existingComments, newComment];
  }

  let insertIndex = parentIndex + 1;
  while (
    insertIndex < existingComments.length &&
    String(existingComments[insertIndex].parentCommentId) === String(parentId)
  ) {
    insertIndex += 1;
  }

  return [
    ...existingComments.slice(0, insertIndex),
    newComment,
    ...existingComments.slice(insertIndex),
  ];
}

export default function FeedPostEngagement({
  item,
  feedRevision = 0,
  currentUserId,
  canModerateComments = false,
  onPatch,
  movie,
  viewerHasWatchedMovie = false,
  inWatchlist = false,
  watchlistEntryId,
  onAddToWatchMovieList,
  onRemoveFromWatchlist,
  onHideFromFeed,
}) {
  const { showToast } = useToast();
  const watchedMovieId = item.id;
  const likeCount = item.likeCount ?? 0;
  const commentCount = item.commentCount ?? 0;
  const liked = item.likedByViewer === true;
  const isOwnPost =
    Boolean(currentUserId) &&
    Boolean(item?.actor?.id) &&
    String(item.actor.id) === String(currentUserId);

  const [likeBusy, setLikeBusy] = useState(false);
  const [bookmarkBusy, setBookmarkBusy] = useState(false);

  const [comments, setComments] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [commentsFetchComplete, setCommentsFetchComplete] = useState(true);
  const [loadingPrev, setLoadingPrev] = useState(false);
  const [composer, setComposer] = useState('');
  const [replyTo, setReplyTo] = useState(null);
  const [posting, setPosting] = useState(false);
  const [deletingCommentId, setDeletingCommentId] = useState(null);
  const [pendingDeleteCommentId, setPendingDeleteCommentId] = useState(null);
  const [error, setError] = useState(null);
  const composerRef = useRef(null);
  const prevFeedRevision = useRef(feedRevision);

  const [reportTarget, setReportTarget] = useState(null);

  const loadInitial = useCallback(
    async ({ softError = false } = {}) => {
      if (!softError) {
        setCommentsFetchComplete(false);
      }
      try {
        const params = new URLSearchParams({
          watchedMovieId,
          limit: '20',
        });
        const res = await fetch(`/api/feed/comments?${params}`, {
          credentials: 'include',
        });
        const data = await readJson(res);
        if (!res.ok || !data.ok) {
          throw new Error(data.error || 'Failed to load comments');
        }
        setComments(data.results || []);
        setNextCursor(data.nextCursor || null);
        setHasMore(data.hasMore === true);
        setError(null);
      } catch (e) {
        if (softError) {
          /* keep existing list; optional background refresh failure */
        } else {
          setError((e && e.message) || 'Failed to load comments');
          setComments([]);
        }
      } finally {
        if (!softError) {
          setCommentsFetchComplete(true);
        }
      }
    },
    [watchedMovieId],
  );

  useEffect(() => {
    setComments([]);
    setNextCursor(null);
    setHasMore(false);
    setCommentsFetchComplete(true);
    setError(null);
    setReplyTo(null);
    loadInitial({ softError: false });
  }, [loadInitial, watchedMovieId]);

  const focusComposer = useCallback(() => {
    composerRef.current?.focus();
  }, []);

  useEffect(() => {
    if (feedRevision === prevFeedRevision.current) return;
    prevFeedRevision.current = feedRevision;
    loadInitial({ softError: true });
  }, [feedRevision, loadInitial]);

  const loadPrevious = useCallback(async () => {
    if (!nextCursor || !hasMore || loadingPrev) return;
    setLoadingPrev(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        watchedMovieId,
        limit: '20',
        cursor: nextCursor,
      });
      const res = await fetch(`/api/feed/comments?${params}`, {
        credentials: 'include',
      });
      const data = await readJson(res);
      if (!res.ok || !data.ok) {
        throw new Error(data.error || 'Failed to load older comments');
      }
      const batch = data.results || [];
      setComments((prev) => [...prev, ...batch]);
      setNextCursor(data.nextCursor || null);
      setHasMore(data.hasMore === true);
    } catch (e) {
      setError((e && e.message) || 'Failed to load older comments');
    } finally {
      setLoadingPrev(false);
    }
  }, [watchedMovieId, nextCursor, hasMore, loadingPrev]);

  const postComment = useCallback(async () => {
    const body = composer.trim();
    if (!body || posting) return;
    setPosting(true);
    setError(null);
    try {
      const res = await fetch('/api/feed/comments', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({watchedMovieId, body, ...(replyTo ? { parentCommentId: replyTo.id } : {}),}),
      });
      const data = await readJson(res);
      if (!res.ok || !data.ok) {
        throw new Error(data.error || 'Failed to post');
      }
      const c = data.comment;
      if (c) {
        setComments((prev) => insertCommentWithThreadOrder(prev, c));
      }
      const n = Number(data.commentCount);
      if (!Number.isNaN(n)) {
        onPatch({ commentCount: n });
      }
      setComposer('');
      setReplyTo(null);
    } catch (e) {
      setError((e && e.message) || 'Failed to post');
    } finally {
      setPosting(false);
    }
  }, [composer, posting, watchedMovieId, onPatch, replyTo]);

  const deleteComment = useCallback(
    async (commentId) => {
      if (!commentId || deletingCommentId) return;
      setError(null);
      setDeletingCommentId(commentId);
      try {
        const res = await fetch(`/api/feed/comments/${commentId}`, {
          method: 'DELETE',
          credentials: 'include',
        });
        const data = await readJson(res);
        if (!res.ok || !data.ok) {
          throw new Error(data.error || 'Failed to delete');
        }
        setComments((prev) =>
          prev.filter(
            (comment) =>
              String(comment.id) !== String(commentId) &&
              String(comment.parentCommentId) !== String(commentId),
          ),
        );
        const n = Number(data.commentCount);
        if (!Number.isNaN(n)) {
          onPatch({ commentCount: n });
        }
      } catch (e) {
        setError((e && e.message) || 'Failed to delete');
      } finally {
        setDeletingCommentId(null);
      }
    },
    [onPatch, deletingCommentId],
  );

  const confirmDeleteComment = useCallback(async () => {
    if (!pendingDeleteCommentId) return;
    const id = pendingDeleteCommentId;
    await deleteComment(id);
    setPendingDeleteCommentId(null);
  }, [pendingDeleteCommentId, deleteComment]);

  const toggleLike = useCallback(async () => {
    if (likeBusy) return;
    const prevLiked = liked;
    const prevCount = likeCount;
    onPatch({
      likedByViewer: !prevLiked,
      likeCount: prevLiked ? Math.max(0, prevCount - 1) : prevCount + 1,
    });
    setLikeBusy(true);
    try {
      const res = await fetch('/api/feed/like', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ watchedMovieId }),
      });
      const data = await readJson(res);
      if (!res.ok || !data.ok) {
        throw new Error(data.error || 'Could not update like');
      }
      onPatch({
        likedByViewer: data.liked === true,
        likeCount: Number(data.likeCount) || 0,
      });
    } catch (_e) {
      onPatch({
        likedByViewer: prevLiked,
        likeCount: prevCount,
      });
    } finally {
      setLikeBusy(false);
    }
  }, [likeBusy, liked, likeCount, onPatch, watchedMovieId]);

  const toggleWatchlistBookmark = useCallback(async () => {
    if (viewerHasWatchedMovie || bookmarkBusy) return;
    setBookmarkBusy(true);
    try {
      if (inWatchlist) {
        if (watchlistEntryId && onRemoveFromWatchlist) {
          await onRemoveFromWatchlist(watchlistEntryId);
          showToast('Removed from watchlist');
        }
      } else if (movie && onAddToWatchMovieList) {
        await onAddToWatchMovieList(movie);
        showToast('Saved to watchlist');
      }
    } finally {
      setBookmarkBusy(false);
    }
  }, [
    bookmarkBusy,
    viewerHasWatchedMovie,
    inWatchlist,
    movie,
    onAddToWatchMovieList,
    onRemoveFromWatchlist,
    watchlistEntryId,
    showToast,
  ]);

  return (
    <>
      <div className="flique-feed-engage">
        <button
          type="button"
          className={`flique-feed-engage-like${liked ? ' flique-feed-engage-like--on' : ''}`}
          onClick={toggleLike}
          disabled={likeBusy}
          aria-pressed={liked}
          aria-label={liked ? 'Unlike' : 'Like'}
        >
          <span className="flique-feed-engage-like-icon" aria-hidden="true">
            {liked ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
              </svg>
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
            )}
          </span>
          <span className="flique-feed-engage-count">{likeCount}</span>
        </button>
        <button
          type="button"
          className="flique-feed-engage-comments-btn flique-feed-engage-comments-btn--open"
          onClick={focusComposer}
          aria-expanded="true"
          aria-controls={`flique-feed-comments-${watchedMovieId}`}
        >
          <span className="flique-feed-engage-comments-icon" aria-hidden="true">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21 15a2 2 0 0 1-2 2H8l-4 4V5a2 2 0 0 1 2-2h13a2 2 0 0 1 2 2z" />
            </svg>
          </span>
          <span className="flique-feed-engage-count">{commentCount}</span>
        </button>
        {viewerHasWatchedMovie ? (
          <span
            className="flique-feed-engage-watched-badge"
            role="status"
            aria-label="You watched this movie"
          >
            <span className="flique-feed-engage-watched-icon" aria-hidden="true">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.25"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M20 6L9 17l-5-5" />
              </svg>
            </span>
            <span className="flique-feed-engage-watched-text">Watched</span>
          </span>
        ) : (
          <button
            type="button"
            className="flique-feed-engage-bookmark-btn"
            onClick={toggleWatchlistBookmark}
            disabled={bookmarkBusy || (inWatchlist && !watchlistEntryId)}
            aria-pressed={inWatchlist}
            aria-label={
              inWatchlist ? 'Remove from watchlist' : 'Save to watchlist'
            }
          >
            <span className="flique-feed-engage-bookmark-icon" aria-hidden="true">
              {inWatchlist ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16z" />
                </svg>
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16z" />
                </svg>
              )}
            </span>
          </button>
        )}
        {currentUserId && (
          <>
            {item.hiddenFromFeed ? (
              <span
                className="flique-feed-hidden-indicator"
                aria-label="Post hidden from feed"
                title="Post hidden from feed"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20C7 20 2.73 16.89 1 12c.85-2.4 2.37-4.48 4.34-6" />
                  <path d="M9.9 4.24A11.03 11.03 0 0 1 12 4c5 0 9.27 3.11 11 8a11.34 11.34 0 0 1-2.15 3.19" />
                  <path d="M14.12 14.12A3 3 0 0 1 9.88 9.88" />
                  <line x1="1" y1="1" x2="23" y2="23" />
                </svg>
              </span>
            ) : null}
            <ThreeDotMenu
              ariaLabel="Post options"
              items={[
                ...(onHideFromFeed ? [{
                  label: item.hiddenFromFeed ? 'Show in feed' : 'Hide from feed',
                  onClick: onHideFromFeed,
                }] : []),
                ...(!isOwnPost
                  ? [{
                      label: 'Report post',
                      danger: true,
                      onClick: () =>
                        setReportTarget({ contentType: 'post', contentId: watchedMovieId }),
                    }]
                  : []),
              ]}
            />
          </>
        )}
      </div>

      {reportTarget && (
        <ReportModal
          contentType={reportTarget.contentType}
          contentId={reportTarget.contentId}
          onClose={() => setReportTarget(null)}
        />
      )}

      <section
        id={`flique-feed-comments-${watchedMovieId}`}
        className="flique-feed-comments-panel"
        aria-label="Comments on this post"
      >
          {error ? (
            <div className="alert alert-danger py-2 small mb-2" role="alert">
              {error}
            </div>
          ) : null}

          <div
            className="flique-feed-comments-scroll"
            aria-busy={!commentsFetchComplete}
          >
            {hasMore ? (
              <button
                type="button"
                className="flique-feed-comments-prev btn btn-link btn-sm px-0"
                onClick={loadPrevious}
                disabled={loadingPrev}
              >
                {loadingPrev ? 'Loading…' : 'Show previous comments'}
              </button>
            ) : null}

            {!commentsFetchComplete ? null : comments.length === 0 ? null : (
              <ul className="list-unstyled mb-0 flique-feed-comments-list">
                {comments.map((c) => {
                  const isMine =
                    currentUserId &&
                    c.author?.id &&
                    String(c.author.id) === String(currentUserId);
                  const canDelete = isMine || canModerateComments;
                  const isReply = Boolean(c.parentCommentId);
                  return (
                    <li
                      key={c.id}
                      className={
                        isMine
                          ? `flique-feed-comment${isReply ? ' flique-feed-comment--reply' : ''} flique-feed-comment--mine`
                          : `flique-feed-comment${isReply ? ' flique-feed-comment--reply' : ''}`
                      }
                    >
                      <div className="flique-feed-comment-row">
                        <span
                          className="flique-tab-icon flique-tab-icon-avatar flique-feed-comment-avatar"
                          aria-hidden="true"
                        >
                          {userTabInitials(c.author)}
                        </span>
                        <div className="flique-feed-comment-content">
                          <div className="flique-feed-comment-meta">
                            <span className="flique-feed-comment-user">
                              {isMine
                                ? 'You'
                                : `@${c.author?.username || 'user'}`}
                            </span>
                            <span className="flique-feed-comment-meta-sep" aria-hidden="true">
                              ·
                            </span>
                            <time
                              className="flique-feed-comment-time text-muted"
                              dateTime={c.createdAt || undefined}
                              title={formatDetailedTimestamp(c.createdAt) || undefined}
                              aria-label={
                                formatDetailedTimestamp(c.createdAt) || undefined
                              }
                            >
                              {formatRelativeTime(c.createdAt) || '—'}
                            </time>
                          </div>
                          <p className="flique-feed-comment-body mb-0">{c.body}</p>
                        </div>
                        {!isReply || canDelete ? (
                          <div className="flique-feed-comment-row-actions">
                            {!isReply ? (
                              <button
                                type="button"
                                className="flique-feed-comment-reply-btn"
                                onClick={() => {
                                  setReplyTo((prev) => {
                                    if (prev?.id === c.id) {
                                      return null;
                                    }
                                    return c;
                                  });
                                  if (replyTo?.id !== c.id) {
                                    composerRef.current?.focus();
                                  }
                                }}
                              >
                                Reply
                              </button>
                            ) : null}
                            {canDelete ? (
                              <button
                                type="button"
                                className="flique-icon-remove-x"
                                aria-label="Delete comment"
                                disabled={Boolean(deletingCommentId)}
                                onClick={() => setPendingDeleteCommentId(c.id)}
                              >
                                ×
                              </button>
                            ) : !isReply ? (
                              <span
                                className="flique-icon-remove-x flique-icon-remove-x--ghost"
                                aria-hidden="true"
                              >
                                ×
                              </span>
                            ) : null}
                            {currentUserId && !canDelete && (
                              <ThreeDotMenu
                                ariaLabel="Comment options"
                                items={[
                                  {
                                    label: 'Report comment',
                                    danger: true,
                                    onClick: () =>
                                      setReportTarget({ contentType: 'comment', contentId: c.id }),
                                  },
                                ]}
                              />
                            )}
                          </div>
                        ) : null}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div className="flique-feed-comments-compose">
            {replyTo ? (
              <div className="flique-feed-replying-to small text-muted mb-1">
                Replying to @{replyTo.author?.username || 'user'}
                <button
                  type="button"
                  className="flique-feed-reply-cancel"
                  onClick={() => setReplyTo(null)}
                  aria-label="Cancel reply"
                >
                  ×
                </button>
              </div>
            ) : null}
            <label
              className="form-label small mb-1 visually-hidden"
              htmlFor={`flique-feed-compose-${watchedMovieId}`}
            >
              Write a comment
            </label>
            <div className="flique-feed-comments-compose-row">
              <textarea
                ref={composerRef}
                id={`flique-feed-compose-${watchedMovieId}`}
                className="form-control form-control-sm flique-feed-comments-input"
                rows={1}
                maxLength={500}
                placeholder="Write a comment…"
                value={composer}
                onChange={(e) => setComposer(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    if (!posting && composer.trim()) {
                      postComment();
                    }
                  }
                }}
              />
              <button
                type="button"
                className="flique-feed-comments-post"
                disabled={posting || !composer.trim()}
                onClick={postComment}
              >
                {posting ? '…' : 'Post'}
              </button>
            </div>
          </div>
      </section>
      {pendingDeleteCommentId ? (
        <div className="flique-modal-backdrop">
          <div className="flique-modal card flique-card flique-friends-modal flique-remove-friend-modal">
            <div className="card-body text-center">
              <h2 className="h5 mb-2">Delete comment?</h2>
              <p className="small text-muted mb-3">
                Are you sure you want to delete that comment?
              </p>
              <div className="d-flex gap-2 justify-content-center flique-remove-friend-actions">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-light flique-remove-friend-action-btn"
                  onClick={() => setPendingDeleteCommentId(null)}
                  disabled={Boolean(deletingCommentId)}
                >
                  No
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-flique flique-remove-friend-action-btn"
                  onClick={confirmDeleteComment}
                  disabled={Boolean(deletingCommentId)}
                >
                  Yes
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
